/**
 * LoginWorkspacePicker — Auswahl-Seite an `/login`, wenn der User keinen
 * direkten Workspace-Link benutzt hat. Ersetzt den früheren `LoginRolePicker`
 * (Fachperson / Patient), der zu generisch war: die Trennung zwischen
 * Klinik und Praxis wurde erst eine Stufe später im ModePicker getroffen,
 * und der Admin-Workspace tauchte gar nicht auf.
 *
 * Vier explizite Optionen:
 *   • Plattform-Admin (Icarus-intern)
 *   • Klinik
 *   • Ambulant
 *   • Patient (kleiner gestaltet, am Fuß der Karten)
 *
 * Die Komponente ist stateless — der Container (`Login.tsx`) navigiert
 * nach `/login/<workspace>`, sobald eine Karte geklickt wird.
 */

import { Link } from 'react-router-dom';
import { ArrowLeft, Building2, Heart, Shield, Stethoscope } from 'lucide-react';
import type { Workspace } from '@/features/post-login-redirect';

interface Props {
  onPick: (workspace: Workspace) => void;
}

interface CardSpec {
  workspace: Workspace;
  title: string;
  description: string;
  icon: typeof Stethoscope;
  iconBg: string;
  iconColor: string;
}

const WORKFLOW_CARDS: readonly CardSpec[] = [
  {
    workspace: 'admin',
    title: 'Plattform-Admin',
    description: 'Icarus-intern: Organisationen, Preise, Audit, Datenpannen.',
    icon: Shield,
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-700',
  },
  {
    workspace: 'klinik',
    title: 'Klinik',
    description: 'Klinik-Personal: Stationen, Konsile, Screening, KPI.',
    icon: Building2,
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-700',
  },
  {
    workspace: 'ambulant',
    title: 'Ambulant',
    description: 'Freiberufliche Praxen: Rechnungen, Telemedizin, Programme.',
    icon: Stethoscope,
    iconBg: 'bg-primary/10',
    iconColor: 'text-primary',
  },
];

export function LoginWorkspacePicker({ onPick }: Props) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-green-50/60 via-background to-amber-50/30 p-4">
      <div className="w-full max-w-2xl space-y-8">
        <div className="text-center space-y-3">
          <img
            src="/icarus_logo_600px.webp"
            alt="Icarus Health"
            className="mx-auto h-14 w-14 rounded-full object-cover"
          />
          <div>
            <h1 className="flex items-baseline justify-center gap-2">
              <span className="font-serif text-2xl font-semibold tracking-[-0.01em] text-ink">Icarus</span>
              <span className="font-mono text-xs font-semibold tracking-[0.14em] text-ink-tertiary-2">D&D</span>
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              Wählen Sie Ihren Arbeitsbereich
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {WORKFLOW_CARDS.map(({ workspace, title, description, icon: Icon, iconBg, iconColor }) => (
            <button
              key={workspace}
              onClick={() => onPick(workspace)}
              className="group bg-card rounded-2xl border-2 border-gray-200 p-5 text-left hover:border-primary hover:shadow-lg transition-all duration-300"
            >
              <div className={`w-11 h-11 rounded-xl ${iconBg} flex items-center justify-center mb-3 group-hover:opacity-90 transition-opacity`}>
                <Icon className={`w-5 h-5 ${iconColor}`} />
              </div>
              <h2 className="text-base font-semibold text-gray-900">{title}</h2>
              <p className="text-xs text-gray-600 mt-1 leading-relaxed">{description}</p>
            </button>
          ))}
        </div>

        {/* Patient-Login bekommt eine eigene, ruhigere Sektion — er hat
            einen anderen Auth-Backend (supabasePortal) und gehört
            visuell nicht zu den Fachperson-Workspaces. */}
        <div className="rounded-xl border border-border bg-card/60 p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center shrink-0">
            <Heart className="w-5 h-5 text-green-700" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-800">Patient</p>
            <p className="text-xs text-gray-600 leading-relaxed">
              Zugang zu Ihrer Ernährungstherapie und Ihrem Tagebuch.
            </p>
          </div>
          <button
            onClick={() => onPick('patient')}
            className="rounded-xl bg-green-700 text-white text-sm font-medium h-9 px-4 hover:bg-green-800 transition-colors shrink-0"
          >
            Anmelden
          </button>
        </div>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-border" />
          </div>
          <div className="relative flex justify-center">
            <span className="bg-gradient-to-br from-green-50/60 via-background to-amber-50/30 px-3 text-xs text-muted-foreground">
              oder
            </span>
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card/60 p-4 text-center space-y-3">
          <div>
            <p className="text-sm font-medium text-gray-800">Demo vereinbaren</p>
            <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
              Persönliche Produktpräsentation mit Echtdaten und echten Workflows
            </p>
          </div>
          <a
            href="mailto:info@icarus-health.de?subject=Demo-Anfrage%20D%3AE%3AF%3AY"
            className="inline-flex items-center justify-center gap-2 w-full rounded-xl bg-primary text-primary-foreground text-sm font-medium h-10 px-4 hover:bg-primary/90 transition-colors"
          >
            Demo anfragen
          </a>
        </div>

        <div className="text-center">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-800 transition-colors"
          >
            <ArrowLeft className="w-3 h-3" />
            Zurück zur Startseite
          </Link>
        </div>

        <p className="text-center text-xs text-gray-500">
          Alle Daten werden DSGVO-konform auf EU-Servern gespeichert.
        </p>
      </div>
    </div>
  );
}
